/**
 * Help Content Registry
 * 
 * Allows plugins to register their help articles for display in Help Center
 */

import { HelpArticleData } from './types';

type RegistryListener = (articles: HelpArticleData[]) => void;

class HelpContentRegistry {
  private articles: Map<string, HelpArticleData[]> = new Map();
  private listeners: Set<RegistryListener> = new Set();

  /**
   * Register help articles from a plugin
   */
  register(pluginId: string, articles: HelpArticleData[]) {
    if (!Array.isArray(articles)) {
      console.warn(`[HelpContentRegistry] Invalid articles array for plugin ${pluginId}`);
      return;
    }
    
    this.articles.set(pluginId, articles);
    const totalArticles = this.getAllArticles();
    console.log(`[HelpContentRegistry] Registered ${articles.length} help articles from plugin ${pluginId} (total: ${totalArticles.length})`);
    
    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Subscribe to registry updates
   */
  subscribe(listener: RegistryListener): () => void {
    this.listeners.add(listener);
    // Immediately call with current articles
    listener(this.getAllArticles());
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners() {
    const allArticles = this.getAllArticles();
    this.listeners.forEach(listener => {
      try {
        listener(allArticles);
      } catch (e) {
        console.error('[HelpContentRegistry] Error in listener:', e);
      }
    });
  }

  /**
   * Get all registered articles from all plugins
   */
  getAllArticles(): HelpArticleData[] {
    const allArticles: HelpArticleData[] = [];
    for (const articles of this.articles.values()) {
      allArticles.push(...articles);
    }
    return allArticles;
  }

  /**
   * Get articles for a specific plugin
   */
  getPluginArticles(pluginId: string): HelpArticleData[] {
    return this.articles.get(pluginId) || [];
  }

  /**
   * Clear all registered articles (useful for testing)
   */
  clear() {
    this.articles.clear();
    this.notifyListeners();
  }
}

// Singleton instance
export const helpContentRegistry = new HelpContentRegistry();

